import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Link as ReactRouterLink } from 'react-router-dom'

import {
  CircularProgress,
  Container,
  Box,
  Button,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link as MuiLink,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog } from '@fortawesome/pro-regular-svg-icons'

import { PageTitle } from '@scoir/headers'
import { ConfirmationDialog } from '@scoir/modals'

import api from 'common/api'
import { isDownload, isAPIActive, isSFTPSlate, isSFTPActive, isSlateActive } from 'common/integrations-helpers'

import oneToOneSvg from './delivery_doc_to_pdf_one_to_one.svg'
import manyToOneSvg from './delivery_doc_to_pdf_many_to_one.svg'

const DEFAULT_INTEGRATIONS = {
  ConcatenateBatchTranscripts: false,
  SlateConfigFileRequired: false,
  ForceNewEasySlateIndexFields: false,
}

const INTEGRATIONS_URL = (scid) => `/api/colleges/${scid}/integrations`

const StyledFormLabel = withStyles({
  //reset bootstrap global styles
  root: {
    borderBottom: 0,
    marginBottom: 0,
  },
})(FormLabel)

const ActionButton = withStyles({
  outlined: {
    background: '#fff',
  },
})(Button)

export class DeliveryPreferences extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loaded: false,
      integrations: DEFAULT_INTEGRATIONS,
      loading: {
        ConcatenateBatchTranscripts: false,
        SlateConfigFileRequired: false,
        ForceNewEasySlateIndexFields: false,
      },
      newFieldConfirmOpen: false,
    }
  }
  componentDidMount() {
    document.title = 'Scoir - Delivery Preferences'
    this.loadIntegrations(this.props.scid)
  }
  async toggleLoading(field) {
    const toggle = () => {
      this.setState({
        loading: {
          ...this.state.loading,
          [field]: !this.state.loading[field],
        },
      })
    }
    return this.state.loading[field] ? setTimeout(toggle, 750) : toggle()
  }
  updateIntegrationsConfig(field, bool) {
    if (this.state.integrations[field] !== bool) {
      this.setStateIntegrations({ [field]: bool })
      this.saveConfig(field, bool)
    }
  }
  setStateIntegrations(fields) {
    this.setState({
      loaded: true,
      integrations: {
        ...this.state.integrations,
        ...fields,
      },
    })
  }
  saveConfig(field, bool) {
    const scid = this.props.scid
    const method = bool ? 'put' : 'delete'
    const BATCH_URL = `${INTEGRATIONS_URL(scid)}/batch`
    const ENDPOINTS = {
      ConcatenateBatchTranscripts: `${BATCH_URL}/concatenatetranscripts`,
      SlateConfigFileRequired: `${BATCH_URL}/slateconfigfile`,
      ForceNewEasySlateIndexFields: `${BATCH_URL}/neweasyslateindex`,
    }
    this.toggleLoading(field)
    api[method](ENDPOINTS[field])
      .then(() => {
        this.toggleLoading(field)
        this.loadIntegrations(scid)
      })
      .catch((err) => {
        this.setState({
          error: true,
          errorResponse: err,
        })
      })
  }
  loadIntegrations(scid) {
    api
      .get(INTEGRATIONS_URL(scid))
      .then((integrations) => {
        this.setStateIntegrations(integrations)
      })
      .catch(() => {
        this.setState({
          loaded: true,
          error: true,
          integrations: DEFAULT_INTEGRATIONS,
        })
      })
  }
  render() {
    const slateActive = isSlateActive(this.state.integrations)
    const canOverrideIndex =
      this.state.integrations.CanUseLegacyIndex &&
      (isDownload(this.state.integrations) ||
        isAPIActive(this.state.integrations) ||
        (isSFTPActive(this.state.integrations) && !isSFTPSlate(this.state.integrations)))
    const canConfigureIndex = !this.state.integrations.ForceNewEasySlateIndexFields && canOverrideIndex
    const canConfigureManifest = canConfigureIndex && this.state.integrations.SlateConfigFileRequired
    // const invokeIfInactive = fn => !isSlateActive && fn()
    return (
      <Container
        className="route-delivery-preferences"
        maxWidth="md"
      >
        <PageTitle
          titleProps={{ component: 'h1' }}
          title="Delivery Preferences"
          actions={
            <ActionButton
              component={MuiLink}
              href="https://scoir.helpdocs.io/category/cqsgqg73lb-documents-delivery"
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="outlined"
            >
              Need Help?
            </ActionButton>
          }
        />
        <Typography paragraph>
          Scoir will prepare your documents for manual &amp; batch downloads according to your preferences.
        </Typography>
        <Typography paragraph>Configure your downloads using the options below.</Typography>
        {!this.state.loaded && (
          <Box
            p={8}
            textAlign="center"
          >
            <CircularProgress
              role="status"
              aria-label="Loading"
              size={50}
            />
          </Box>
        )}
        {this.state.loaded && (
          <Box
            component={Paper}
            display="flex"
            mt={3}
            p={2}
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            <Box
              margin="50px"
              width="140px"
            >
              <Box height="113px">
                <Fade
                  timeout={200}
                  in={!this.state.integrations.ConcatenateBatchTranscripts}
                >
                  <Box
                    display={!this.state.integrations.ConcatenateBatchTranscripts ? 'block' : 'none'}
                    width="140px"
                  >
                    <img
                      src={oneToOneSvg}
                      alt="one to one"
                    />
                  </Box>
                </Fade>
                <Fade
                  timeout={200}
                  in={this.state.integrations.ConcatenateBatchTranscripts}
                >
                  <Box
                    display={this.state.integrations.ConcatenateBatchTranscripts ? 'block' : 'none'}
                    width="140px"
                  >
                    <img
                      src={manyToOneSvg}
                      alt="many to one"
                    />
                  </Box>
                </Fade>
              </Box>
            </Box>
            <Box>
              {slateActive && (
                <Box mb={4}>
                  <Typography>
                    Your Delivery Preferences have been preconfigured based on Slate&apos;s requirements.
                  </Typography>
                </Box>
              )}
              <Box mb={4}>
                <FormControl component="fieldset">
                  <StyledFormLabel component="legend">How would you like your files created?</StyledFormLabel>
                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={16}
                  >
                    <Fade in={this.state.loading.ConcatenateBatchTranscripts}>
                      <CircularProgress
                        role="status"
                        aria-label="Loading"
                        size={20}
                      />
                    </Fade>
                    <RadioGroup
                      aria-label="select pdf delivery options"
                      name="pdfOptions"
                      value={this.state.integrations.ConcatenateBatchTranscripts ? 'many-to-one' : 'one-to-one'}
                      onChange={(e) => {
                        const isConcatenateBatchTranscripts = e.target.value === 'many-to-one' ? true : false
                        this.updateIntegrationsConfig('ConcatenateBatchTranscripts', isConcatenateBatchTranscripts)
                      }}
                    >
                      <FormControlLabel
                        value="one-to-one"
                        control={<Radio color="primary" />}
                        label="As individual .PDFs for each document"
                        disabled={slateActive}
                      />
                      <FormControlLabel
                        value="many-to-one"
                        control={<Radio color="primary" />}
                        label="Together as a single .PDF"
                        disabled={slateActive}
                      />
                    </RadioGroup>
                  </Box>
                </FormControl>
              </Box>
              <ConfirmationDialog
                open={this.state.newFieldConfirmOpen}
                headerText="Confirmation of change"
                cancellationText="Keep as is"
                confirmationText="Continue"
                onClose={() =>
                  this.setState({
                    newFieldConfirmOpen: false,
                  })
                }
                onConfirm={() => {
                  this.updateIntegrationsConfig('ForceNewEasySlateIndexFields', true)
                  this.setState({
                    newFieldConfirmOpen: false,
                  })
                }}
              >
                <Typography>
                  This action will replace all prior configuration settings, including index file formatting and student
                  manifest inclusions.
                </Typography>
              </ConfirmationDialog>
              {canOverrideIndex && (
                <Box mb={4}>
                  <FormControl component="fieldset">
                    <StyledFormLabel component="legend">
                      Would you like document metadata and details included in your index file?
                    </StyledFormLabel>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={16}
                    >
                      <Fade in={this.state.loading.ForceNewEasySlateIndexFields}>
                        <CircularProgress
                          role="status"
                          aria-label="Loading"
                          size={20}
                        />
                      </Fade>
                      <RadioGroup
                        aria-label="select field options"
                        name="newFields"
                        value={
                          this.state.integrations.ForceNewEasySlateIndexFields ? 'yes-new-fields' : 'no-new-fields'
                        }
                        onChange={(e) => {
                          const isForceNewEasySlateIndexFields = e.target.value === 'yes-new-fields' ? true : false
                          if (isForceNewEasySlateIndexFields) {
                            this.setState({ newFieldConfirmOpen: true })
                          } else {
                            this.updateIntegrationsConfig(
                              'ForceNewEasySlateIndexFields',
                              isForceNewEasySlateIndexFields,
                            )
                          }
                        }}
                      >
                        <FormControlLabel
                          value="yes-new-fields"
                          control={<Radio color="primary" />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no-new-fields"
                          control={<Radio color="primary" />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>
                  </FormControl>
                </Box>
              )}
              {canConfigureIndex && (
                <Box mb={4}>
                  <FormControl component="fieldset">
                    <StyledFormLabel component="legend">How would you like your index file formatted?</StyledFormLabel>
                    <Box mt={1}>
                      <Button
                        component={ReactRouterLink}
                        color="primary"
                        variant="outlined"
                        size="small"
                        disabled={slateActive}
                        startIcon={
                          <FontAwesomeIcon
                            size="sm"
                            icon={faCog}
                          />
                        }
                        to={`${import.meta.env.BASE_URL}/documents/delivery/preferences/batch-index-file`}
                      >
                        Configure Index File Fields
                      </Button>
                    </Box>
                  </FormControl>
                </Box>
              )}
              {canConfigureManifest && (
                <Box mb={4}>
                  <FormControl component="fieldset">
                    <StyledFormLabel component="legend">Would you like a student manifest included?</StyledFormLabel>

                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={16}
                    >
                      <Fade in={this.state.loading.SlateConfigFileRequired}>
                        <CircularProgress
                          role="status"
                          aria-label="Loading"
                          size={20}
                        />
                      </Fade>
                      <RadioGroup
                        aria-label="select manifest options"
                        name="manifestOptions"
                        disabled={slateActive}
                        value={this.state.integrations.SlateConfigFileRequired ? 'yes-manifest' : 'no-manifest'}
                        onChange={(e) => {
                          const isSlateConfigFileRequired = e.target.value === 'yes-manifest' ? true : false
                          this.updateIntegrationsConfig('SlateConfigFileRequired', isSlateConfigFileRequired)
                        }}
                      >
                        <FormControlLabel
                          value="yes-manifest"
                          control={<Radio color="primary" />}
                          label="Yes"
                          disabled={slateActive}
                        />
                        <FormControlLabel
                          value="no-manifest"
                          control={<Radio color="primary" />}
                          label="No"
                          disabled={slateActive}
                        />
                      </RadioGroup>
                    </Box>
                  </FormControl>
                </Box>
              )}
              <Typography color="textSecondary">
                Your changes will be reflected in the next scheduled download.
              </Typography>
            </Box>
          </Box>
        )}
      </Container>
    )
  }
}

DeliveryPreferences.propTypes = {
  scid: PropTypes.number.isRequired,
}

export default connect((store) => ({
  scid: store.user.record.Scid,
}))(DeliveryPreferences)
